import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Body, Button, Label, RankBadge, Screen, SiteFooter, TextField, cx } from '../components/ui';
import { BarsIcon, GroupIcon, JongtenMark, NoteIcon } from '../components/icons';
import { listStoredGroups } from '../lib/storage';
import { formatDate, formatPoint, formatScore } from '../lib/format';

/**
 * トップ（§9.2）。
 *
 * アカウントがないぶん、端末に保存されたグループがここでの唯一の入口になる。
 * URLを紛失したときのために「共有URLから参加する」を常設。
 *
 * 並びは「卓に戻る」を最優先にしている。週に一度開く人にとって説明は毎回
 * 要らないので、何ができるアプリかの解説はグループの下に置く。グループが
 * 0件のときは自動的に解説が上がってきて、初回の説明順になる。
 */

/*
 * 解説に出すグループ画面の見本。
 *
 * 1対局の結果ではなくグループ画面そのものを出している。半荘1回の精算なら
 * /keisan.html でも同じものが出せるので、「続けて記録すると通算と平均順位が
 * 積み上がる」ところを見せないと、このアプリを使う理由が写らない。
 *
 * 飾りの数字でも整合は取ってある。各対局の合計は 0、通算は3対局の和、
 * 平均順位は実際の順位から計算した値。
 */
const SAMPLE_MEMBERS = ['たろう', 'はなこ', 'じろう', 'さぶろう'];

/** 通算。並びはそのまま順位順 */
const SAMPLE_STANDINGS = [
  { name: 'たろう', total: 64, avg: '2.00' },
  { name: 'はなこ', total: 42, avg: '2.00' },
  { name: 'じろう', total: -4, avg: '2.33' },
  { name: 'さぶろう', total: -102, avg: '3.67' },
];

/** points は SAMPLE_MEMBERS と同じ並び。列がメンバーで固定なのは実画面と同じ */
const SAMPLE_MATCHES = [
  { at: '8/31 21:40', points: [55, 9, -19, -45] },
  { at: '8/24 22:05', points: [31, -8, 22, -45] },
  { at: '8/17 20:50', points: [-22, 41, -7, -12] },
];

/**
 * 使い方の手順3に出す1半荘。上の SAMPLE_MATCHES と参照し合うと、並び順が
 * 一致していることに暗黙で頼ることになるので、独立して持つ。
 *
 * 25,000持ち30,000返し・ウマ10-20 での計算と一致している。
 * 素点の合計は 100,000、精算ポイントの合計は 0。
 */
const SAMPLE_MATCH_INPUT = [
  { name: 'たろう', raw: 45200, point: 55 },
  { name: 'はなこ', raw: 28700, point: 9 },
  { name: 'じろう', raw: 21400, point: -19 },
  { name: 'さぶろう', raw: 4700, point: -45 },
];

const NO_SIGNUP = ['アカウント作成なし', 'アプリのインストール不要', 'URLを送るだけで全員に共有'];

/**
 * ヒーロー直下の紹介。
 *
 * 3枚目に「ランキング」と書かない。グループ画面の一覧は合計ポイント順に
 * 並んでいて順位バッジも付いているが、「ランキング」という語はどこにも
 * 出ていない。ここは画面に実際に出ている語（順位・通算）だけで書く。
 */
const INTRO = [
  { Icon: GroupIcon, title: 'グループで管理', desc: '仲間と一緒に記録を管理。' },
  { Icon: NoteIcon, title: 'カンタン記録', desc: '対局後すぐにスコアを入力。' },
  { Icon: BarsIcon, title: 'スコアを共有', desc: '通算成績と順位をみんなでチェック。' },
];

/** ヒーローの地。状態バーもこの色にして境目を消す */
const HERO_COLOR = '#EFE9DA';
/** 他の画面のヘッダー色。離れるときに戻す */
const DEFAULT_THEME_COLOR = '#123528';

/**
 * 使い方に添えるQRの飾り。位置検出パターン3つと決め打ちの疑似乱数でモジュールを
 * 置いているだけで、読み取れない。
 *
 * 本物を置いてはいけない。実際のQRは共有URLから生成するもので、トップを見ている
 * 時点ではまだ存在しない。読み取れるものを出すと、存在しないグループへ誘導する
 * ことになる。
 *
 * 1本のパスにまとめている。rect を並べると200要素ほどになる。
 */
const QR_PATH = (() => {
  const N = 21;
  const on: boolean[][] = Array.from({ length: N }, () => Array<boolean>(N).fill(false));

  const finder = (ox: number, oy: number) => {
    for (let y = 0; y < 7; y++) {
      for (let x = 0; x < 7; x++) {
        const edge = x === 0 || x === 6 || y === 0 || y === 6;
        const core = x >= 2 && x <= 4 && y >= 2 && y <= 4;
        on[oy + y]![ox + x] = edge || core;
      }
    }
  };
  finder(0, 0);
  finder(N - 7, 0);
  finder(0, N - 7);

  // 位置検出パターンとその余白は埋めない
  const reserved = (x: number, y: number) =>
    (x < 8 && y < 8) || (x >= N - 8 && y < 8) || (x < 8 && y >= N - 8);

  // MINSTD。毎回同じ絵になる
  let seed = 20260902;
  const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;

  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      if (!reserved(x, y) && rnd() < 0.46) on[y]![x] = true;
    }
  }
  // タイミングパターン（本物のQRにある破線）
  for (let i = 8; i < N - 8; i++) {
    on[6]![i] = i % 2 === 0;
    on[i]![6] = i % 2 === 0;
  }

  let d = '';
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      if (on[y]![x]) d += `M${x} ${y}h1v1h-1z`;
    }
  }
  return d;
})();

/**
 * 使い方に添える画面の見本の枠。
 *
 * 実画面のコンポーネントは使わない。埋め込むと state もフォームも生きたまま
 * トップに乗り、押せてしまう入力欄が出るうえ、react-query までバンドルに入る。
 * 見せたいのは形だけなので静的なマークアップで足りる。実画面を変えたときの
 * 追従は手作業になる。
 */
function MiniShot({ maxH, children }: { maxH: string; children: React.ReactNode }) {
  return (
    <div
      className={cx(
        'relative mt-2 overflow-hidden rounded-[10px] bg-bone-hi',
        'shadow-[0_3px_9px_-6px_rgba(18,53,40,.55)]',
        maxH,
      )}
    >
      {/*
        0.8倍に縮める。w-[125%] を掛けてあるので、カードの幅がいくつでも
        横はぴったり埋まる。max-h で下を切り、続きがあることは下のぼかしで示す。
      */}
      <div className="w-[125%] origin-top-left scale-[0.8]">{children}</div>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-b from-transparent to-bone-hi"
      />
    </div>
  );
}

/** 使い方の1手順 */
function HowToStep({
  no,
  title,
  desc,
  children,
}: {
  no: number;
  title: string;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-[rgba(18,53,40,0.14)] pt-4 first:border-t-0 first:pt-0 [&+&]:mt-4">
      <div className="flex items-center gap-2.5">
        {/*
          丸なのは順位バッジ（角丸の四角に数字）と紛れないため。特徴1の中に
          順位バッジが4つ出ている。手順は本当に順序があるので番号自体は妥当。
        */}
        <span className="grid size-[24px] shrink-0 place-items-center rounded-full bg-felt text-[13px] font-bold text-bone-hi">
          {no}
        </span>
        <span className="text-[15.5px] font-bold">{title}</span>
      </div>
      <p className="mt-1.5 text-[13px] leading-relaxed text-ink-soft">{desc}</p>
      {children}
    </div>
  );
}

/**
 * 特徴カードの見出し。中央寄せなのは、左寄せだと見出しの1行目に見えてしまうため。
 *
 * 色は羅紗の中間色。カードの地 (#E4EDE5) に対して 5.87:1 あり、WCAG AA を満たす。
 * 以前使っていた金 (#B8862A) はこの地では 2.71:1 しかなく基準を割っていた。
 * 15px は WCAG では「通常のテキスト」扱いなので、大きくしても基準は 4.5:1 のまま緩まない。
 *
 * 塗りのバッジにはしない。順位バッジ（角丸の四角に数字）と同じ見た目になり、
 * 意味の違う数字が2種類並ぶことになる。
 */
function FeatureLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2.5 text-center text-[15px] font-bold tracking-[0.16em] text-felt-line">
      {children}
    </div>
  );
}

export function TopPage() {
  const navigate = useNavigate();
  const groups = listStoredGroups();
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinUrl, setJoinUrl] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);

  /**
   * トップだけ状態バーを牌の色にする。ヒーローが画面上端まで届くので、
   * 時計の乗っている帯が緑のままだと境目が残ってしまう。
   * 他の画面はヘッダーが羅紗色なので、離れるときに戻す。
   */
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return;
    meta.content = HERO_COLOR;
    return () => {
      meta.content = DEFAULT_THEME_COLOR;
    };
  }, []);

  function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    setJoinError(null);

    // 共有URL: https://{domain}/g/{groupId}#t={token}
    const match = joinUrl.trim().match(/\/g\/([0-9a-f-]{36})(#t=(.+))?$/i);
    if (!match) {
      setJoinError('共有URLの形式が正しくありません');
      return;
    }
    const [, groupId, , token] = match;
    if (!token) {
      setJoinError('URLに参加用のトークン（#t=…）が含まれていません');
      return;
    }
    // 遷移先でフラグメントから読み取って保存される
    navigate(`/g/${groupId}#t=${token}`);
  }

  return (
    <Screen>
      {/*
        ヒーローは Body の外に出して、上端と左右にぴったり付ける。

        地はグラデーション。下の紹介バンド（bone-hi）へ溶かして継ぎ目を消している。
        単色にすると bone と bone-hi の差（1.11）で、線が見えるのに面としては
        分かれきらない中途半端さが残る。上端は bone のままなので、theme-color で
        状態バーに色を合わせている仕掛け（HERO_COLOR）はそのまま生きる。

        上下の余白が非対称なのも意図的。状態バーも同じ牌の色なので、視覚的な面は
        「状態バー＋ヒーロー」ひと続きになる。上下を均等にすると中身が上へ張り付いて
        見えるため、上を厚く取って下端側に寄せている。
      */}
      <div className="relative overflow-hidden bg-gradient-to-b from-bone to-bone-hi px-4 pb-3 pt-6 text-center text-felt">
          {/*
            背景の薄い絵柄。牌・筒・点棒を散らして無地の寂しさを埋める。
            インライン SVG なので追加のリクエストは発生しない。
            slice で領域を覆い、はみ出しは overflow-hidden が切る。
          */}
          <svg
            className="absolute inset-0 size-full opacity-50"
            viewBox="0 0 328 100"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
          >
            <g fill="none" stroke="#123528" strokeOpacity=".13" strokeWidth="2">
              <rect x="14" y="10" width="22" height="30" rx="4" />
              <rect x="278" y="14" width="22" height="30" rx="4" transform="rotate(14 289 29)" />
              <rect x="46" y="62" width="22" height="30" rx="4" transform="rotate(-11 57 77)" />
              <rect x="258" y="60" width="22" height="30" rx="4" transform="rotate(8 269 75)" />
              <circle cx="308" cy="76" r="9" />
              <circle cx="22" cy="72" r="9" />
              <path d="M132 12h15M139 6v13" />
              <path d="M186 84h15M193 78v13" />
            </g>
          </svg>

        {/*
          タグライン「グループで記録する麻雀スコア」はここから外した。下の h1 と
          ほぼ同じことを言っており、2つ並べると同じ話を2回聞かせることになる。
        */}
        <div className="relative flex flex-col items-center gap-2.5">
          <JongtenMark className="w-[35px]" />
          <div className="text-[28px] font-black leading-none tracking-[0.18em]">雀点</div>
        </div>
      </div>

      {/*
        紹介バンド。Body の外に置いているのは、ヒーローと同じく地を左右いっぱいに
        敷きたいため。Body（p-4）の中だと内側に寄ってしまう。

        下の pb-4（16px）と Body の pt-5（20px）を足した 36px が、アイコンと
        「グループをつくる」の間になる。紹介から行動へ切り替わるところなので、
        カード同士の 12px よりはっきり広く取っている。
      */}
      <div className="bg-bone-hi px-4 pb-4 pt-6 text-center text-felt">
        {/* この画面で唯一の h1。これまでトップには見出しレベル1が無かった */}
        <h1 className="text-[21px] font-bold leading-[1.45] tracking-tight">
          グループで記録して、
          <br />
          スコアを共有しよう。
        </h1>

        <div className="mt-5 flex gap-2">
          {INTRO.map(({ Icon, title, desc }) => (
            <div key={title} className="min-w-0 flex-1">
              <div className="mx-auto grid size-[54px] place-items-center rounded-full bg-felt-pale">
                <Icon />
              </div>
              <div className="mt-2 text-[12.5px] font-bold">{title}</div>
              <div className="mt-0.5 text-[11px] leading-[1.6] text-felt-line">{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* pt-5 は紹介バンドの pb-4 と合わせて 36px になる（上のコメント参照）。
          pb-12 はフッターとの間隔。フッターは色が付いた面なので、間隔をあちら側の
          mt で作ると面が浮いて見える。こちらの pb で空ける。 */}
      <Body className="gap-3 pb-12 pt-5">
        {/* --- 入口 --- */}
        <Link to="/new">
          <Button>グループをつくる</Button>
        </Link>

        {joinOpen ? (
          <form onSubmit={handleJoin} className="flex flex-col gap-2.5">
            <TextField
              label="共有URL"
              placeholder="https://…/g/xxxxxxxx#t=…"
              value={joinUrl}
              onChange={(e) => setJoinUrl(e.target.value)}
              autoFocus
              inputMode="url"
            />
            {joinError && <div className="text-xs text-red">{joinError}</div>}
            <Button type="submit" disabled={!joinUrl.trim()}>
              開く
            </Button>
            <Button type="button" variant="ghost" onClick={() => setJoinOpen(false)}>
              やめる
            </Button>
          </form>
        ) : (
          <Button variant="ghost" onClick={() => setJoinOpen(true)}>
            共有URLから参加する
          </Button>
        )}

        {/* --- 参加中のグループ --- */}
        {groups.length > 0 && (
          <>
            <Label className="mt-1">参加中のグループ</Label>
            <div className="flex flex-col gap-2.5">
              {groups.map((g) => (
                <Link
                  key={g.groupId}
                  to={`/g/${g.groupId}`}
                  className="flex items-center gap-3 rounded-xl border border-line bg-white px-3.5 py-3 transition active:scale-[0.995]"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[15px] font-bold">{g.name || 'グループ'}</div>
                    <div className="mt-0.5 text-[11.5px] text-ink-faint">
                      {[
                        g.playerCount ? `${g.playerCount}人` : null,
                        typeof g.matchCount === 'number' ? `${g.matchCount}戦` : null,
                        g.lastMatchAt ? `最終記録 ${formatDate(g.lastMatchAt)}` : null,
                      ]
                        .filter(Boolean)
                        .join('・') || '　'}
                    </div>
                  </div>
                  <span className="ml-auto text-base text-ink-faint">›</span>
                </Link>
              ))}
            </div>
          </>
        )}

        {/*
          --- 何ができるアプリか ---
          カード同士の 12px に対して、ここだけ 46px 空ける。話題が「自分の卓」から
          「このアプリについて」へ変わることを、区切り線を引かずに示す。
        */}
        <div className="mt-[34px] rounded-2xl bg-felt-pale p-3.5 text-felt">
          <FeatureLabel>特徴 1</FeatureLabel>
          <div className="text-[16.5px] font-bold leading-snug tracking-tight">
            素点を入れるだけで
            <br />
            <span className="text-gold">ウマ・オカ・飛び賞</span>まで精算。
          </div>

          {/*
            グループ画面の見本。文字サイズも余白も GroupHomePage と同じ値のまま書き、
            外から zoom で 0.85 倍に縮めている。中身を実寸で持っておくことが、この
            見本が実画面と一致していることの根拠になる。倍率だけ変えれば対応は崩れない。
            （このカードの内側は 390px 幅の端末でちょうど 330px。実画面の ListCard の
            内側（Body p-4 → ListCard px-3.5）と同じ幅なので、実寸で書ける。）

            MiniShot の transform: scale ではなく zoom なのは、こちらは下を切らずに
            全体を見せるから。transform はレイアウトの箱を縮めないので下に 15% ぶんの
            空白が残る。zoom は箱ごと縮む。
            zoom は自分の margin にも掛かるので mt-3 は 10.2px として出る。ここは
            狂ってはいけない値ではないので、打ち消さずにそのままにしている。
          */}
          <div className="relative mt-3 overflow-hidden rounded-[10px] bg-white px-3 py-2.5 text-ink shadow-[0_4px_10px_-8px_rgba(18,53,40,.5)] [zoom:0.85]">
            {/* AppBar。左右いっぱいに出したいので padding のぶん外へ広げる */}
            <div className="-mx-3 -mt-2.5 mb-2.5 flex items-center gap-2.5 bg-felt px-3 pb-3 pt-2.5 text-bone-hi">
              <span aria-hidden className="-ml-1 text-[22px] leading-none text-felt-sub">
                ‹
              </span>
              <div className="min-w-0">
                <div className="truncate text-[17px] font-bold leading-tight">水曜の卓</div>
                <div className="mt-0.5 text-xs text-felt-sub">3戦 · 4人</div>
              </div>
              <div className="ml-auto flex shrink-0 gap-3 text-[13px] font-medium">
                <span className="text-felt-accent">共有</span>
                <span className="text-felt-sub">設定</span>
              </div>
            </div>

            {/* --- 累計ランキング --- */}
            {SAMPLE_STANDINGS.map((s, i) => (
              <div
                key={s.name}
                className={cx('flex items-center gap-3 py-2.5', i > 0 && 'border-t border-line')}
              >
                <RankBadge rank={i + 1} />
                <div className="min-w-0">
                  <div className="text-[15px] font-bold leading-tight">{s.name}</div>
                  <div className="mt-0.5 text-[11px] text-ink-faint">3戦 · 平均 {s.avg}</div>
                </div>
                <span
                  className={cx(
                    'ml-auto text-[23px] font-black leading-none tabular-nums',
                    s.total >= 0 ? 'pt-plus' : 'pt-minus',
                  )}
                >
                  {formatPoint(s.total)}
                </span>
                <span aria-hidden className="shrink-0 text-base text-ink-faint">
                  ›
                </span>
              </div>
            ))}

            {/* --- 最近の対局 ---
                実画面と同じ固定幅（日付 62px・メンバー 52px・gap 8px）。
                max-w で 310px に止めているので、画面が広くても列が間延びしない。 */}
            <Label className="mt-2.5">最近の対局</Label>
            <div className="mt-1 max-w-[310px]">
              <div className="flex items-center gap-2 border-b border-line pb-1.5">
                <span className="w-[62px] shrink-0" />
                {SAMPLE_MEMBERS.map((m) => (
                  <span
                    key={m}
                    className="min-w-0 flex-1 truncate text-right text-[10px] text-ink-faint"
                  >
                    {m}
                  </span>
                ))}
              </div>
              {SAMPLE_MATCHES.map((m, i) => (
                <div
                  key={m.at}
                  className={cx('flex items-center gap-2 py-2.5', i > 0 && 'border-t border-line')}
                >
                  <span className="w-[62px] shrink-0 text-[11px] text-ink-faint">{m.at}</span>
                  {m.points.map((p, j) => (
                    <span
                      key={SAMPLE_MEMBERS[j]}
                      className={cx(
                        'min-w-0 flex-1 text-right text-[13px] font-bold tabular-nums',
                        p >= 0 ? 'pt-plus' : 'pt-minus',
                      )}
                    >
                      {formatPoint(p)}
                    </span>
                  ))}
                </div>
              ))}
            </div>

            {/* --- 適用中のルール --- */}
            <div className="mt-2.5 flex items-center gap-2 rounded-lg bg-bone px-3 py-2.5 text-[11.5px] leading-relaxed text-ink-soft">
              <span>標準ルール／25,000持ち 30,000返し</span>
              <span aria-hidden className="ml-auto shrink-0 text-sm text-ink-faint">
                ›
              </span>
            </div>

            {/* 実画面では sticky で本文の上に浮いている。重なるのは想定どおり */}
            <div className="absolute bottom-3 right-3 rounded-full bg-felt px-5 py-3.5 text-sm font-bold leading-none text-bone-hi shadow-lg shadow-felt/40">
              ＋ 対局を記録
            </div>
          </div>

          <div className="mt-2.5 text-[12px] leading-relaxed text-ink-soft">
            同点なら席順、飛びなら飛ばした人まで、ルールどおりに配分します。
          </div>
        </div>

        {/* --- 始めるハードルの低さ --- */}
        <div className="rounded-2xl bg-felt-pale p-3.5 text-felt">
          <FeatureLabel>特徴 2</FeatureLabel>
          {/* 特徴1の見出しと同じ 16.5px。ラベルの 15px より大きくしておかないと、
              見出しよりラベルのほうが目立って主従が読めなくなる */}
          <h2 className="text-[16.5px] font-black tracking-tight">会員登録もアプリも要りません</h2>
          <ul className="mt-2.5 flex flex-col gap-1.5">
            {NO_SIGNUP.map((t) => (
              <li key={t} className="flex items-center gap-2 text-[13.5px] text-ink-soft">
                <span
                  aria-hidden
                  className="grid size-[15px] shrink-0 place-items-center rounded-full bg-ok text-[9px] font-bold text-white"
                >
                  ✓
                </span>
                {t}
              </li>
            ))}
          </ul>
          {/* 罫線を引かない。区切りの役は空きが持つので、10px のままだとチェック項目の
              続きに見える。14px に広げてある。カード内に残る罫線は下のリンクの1本だけ
              になり、「ここだけ押せる」という違いがかえって出る。 */}
          <div className="mt-3.5 text-[12px] leading-relaxed text-ink-faint">
            ブラウザでそのまま使えます。ホーム画面に追加すればアプリのように開けます。
          </div>

          {/*
            計算機は SPA の外にある独立ページなので Link ではなく <a>。
            Link だとクライアント側遷移になり、ルータが知らないパスとして
            NotFoundPage に落ちる。ここは必ずフルロードさせる。
          */}
          <a
            href="/keisan.html"
            className="mt-2.5 flex items-center gap-1 border-t border-[rgba(18,53,40,0.14)] pt-2.5 text-[13px] font-bold text-felt"
          >
            グループを作成せずに1回だけ計算する
            <span aria-hidden className="text-ink-faint">
              ›
            </span>
          </a>
        </div>

        {/*
          --- 使い方 ---
          見出しを枠の外に出している。中に入れると特徴カードの見出しと同じ役割に
          見えて、淡い緑の面が3枚横並びに読めてしまう。外に出すと牌の色の上に乗るので
          面が一度切れ、「ここから下がひとかたまり」という関係が形で出る。

          Body の gap は 12px。話題が変わるところなので mt-3.5 を足して 26px 空ける。
        */}
        <div className="mt-3.5">
          <h2 className="mb-2.5 text-center text-[17px] font-bold tracking-[0.16em] text-felt">
            雀点の使い方
          </h2>

          <div className="rounded-2xl bg-felt-pale p-3.5 text-felt">
            <HowToStep
              no={1}
              title="グループを作成"
              desc="卓の名前と人数、ウマを選ぶだけ。メンバーはあとから足せます。"
            >
              <MiniShot maxH="max-h-[210px]">
                <div className="bg-bone-hi">
                  <div className="flex items-center gap-2.5 bg-felt px-4 pb-3.5 pt-3 text-bone-hi">
                    <span aria-hidden className="-ml-1 text-[22px] leading-none text-felt-sub">
                      ‹
                    </span>
                    <span className="text-[17px] font-bold leading-tight">グループをつくる</span>
                  </div>
                  <div className="flex flex-col gap-3.5 p-4">
                    <div>
                      <Label>グループ名</Label>
                      <div className="mt-1.5 rounded-[10px] border border-line bg-white px-3 py-2.5 text-[15px]">
                        水曜の卓
                      </div>
                    </div>
                    <div>
                      <Label>人数</Label>
                      <div className="mt-1.5 flex overflow-hidden rounded-lg border border-line bg-white">
                        <span className="flex-1 bg-felt py-2 text-center text-[13px] font-medium text-bone-hi">
                          4人打ち
                        </span>
                        <span className="flex-1 border-l border-line py-2 text-center text-[13px] text-ink-soft">
                          3人打ち
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label>ウマ</Label>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {['5-10', '10-20', '10-30', '20-30'].map((u) => (
                          <span
                            key={u}
                            className={cx(
                              'rounded-full border px-3 py-1 text-xs',
                              u === '10-20'
                                ? 'border-felt bg-felt font-medium text-bone-hi'
                                : 'border-line bg-white text-ink-soft',
                            )}
                          >
                            {u}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </MiniShot>
            </HowToStep>

            <HowToStep
              no={2}
              title="メンバーにリンクを共有"
              desc="共有URLをLINEで送るかQRを読んでもらうだけ。受け取った人の登録も要りません。"
            >
              <MiniShot maxH="max-h-[235px]">
                {/* 実画面は羅紗色45%の幕の上にシートが乗る。上に少し幕を見せる */}
                <div className="bg-felt pt-4">
                  <div className="rounded-t-2xl border-t border-line bg-white px-4 pb-6 pt-5">
                    <div className="text-base font-bold">水曜の卓に招待する</div>
                    <div className="mt-0.5 text-[11.5px] text-ink-faint">
                      読み込んだ人はすぐに記録を追加できます
                    </div>
                    <svg
                      viewBox="0 0 21 21"
                      shapeRendering="crispEdges"
                      role="img"
                      aria-label="参加用QRコードの見本"
                      className="mx-auto mt-4 size-[126px] rounded-lg border border-line bg-white p-2"
                    >
                      <path d={QR_PATH} fill="#232B26" />
                    </svg>
                    <div className="mt-3.5 break-all rounded-lg bg-bone px-3 py-2.5 text-[11.5px] leading-relaxed text-ink-soft">
                      https://jongten.com/g/8f3c1a2e-…#t=…
                    </div>
                  </div>
                </div>
              </MiniShot>
            </HowToStep>

            <HowToStep
              no={3}
              title="対局を記録"
              desc="半荘が終わったら素点を入れるだけ。ウマ・オカ・飛び賞こみの精算がその場で出ます。"
            >
              <MiniShot maxH="max-h-[240px]">
                <div className="flex flex-col gap-2.5 bg-bone-hi p-4">
                  <div className="rounded-xl border border-line bg-white px-3.5 py-1">
                    {SAMPLE_MATCH_INPUT.map((r, i) => (
                      <div
                        key={r.name}
                        className={cx(
                          'flex items-center gap-2.5 py-2.5',
                          i > 0 && 'border-t border-line',
                        )}
                      >
                        <span className="text-[15px] font-bold">{r.name}</span>
                        <span className="ml-auto text-[21px] font-black tabular-nums">
                          {formatScore(r.raw)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 rounded-lg bg-[#E8F0E6] px-3 py-2.5 text-[13px] font-medium text-ok">
                    <span aria-hidden>✓</span>
                    <span>合計 100,000 点</span>
                  </div>
                  <div className="rounded-xl bg-felt px-3.5 py-3 text-bone-hi">
                    <div className="mb-1.5 text-[11.5px] text-felt-sub">計算結果</div>
                    {SAMPLE_MATCH_INPUT.map((r, i) => (
                      <div key={r.name} className="flex items-center gap-2.5 py-1 text-[13.5px]">
                        <RankBadge rank={i + 1} className="size-[19px] text-[11px]" />
                        <span>{r.name}</span>
                        <span
                          className={cx(
                            'ml-auto text-base font-black tabular-nums',
                            r.point >= 0 ? 'text-[#7FD9A8]' : 'text-[#F09A92]',
                          )}
                        >
                          {formatPoint(r.point)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </MiniShot>
            </HowToStep>
          </div>
        </div>

        {/*
          読み終えた位置にもう一度置く。上まで戻らせないため。
          使い方の一部ではなく次の一手なので枠の外に出し、gap 12px に mt-3 を足して
          24px 空けている。
        */}
        <Link to="/new" className="mt-3">
          <Button>グループをつくる</Button>
        </Link>
      </Body>

      <SiteFooter />
    </Screen>
  );
}
